import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Bug, Sparkles, Heart, MoreHorizontal, Send } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/api';

const CATEGORY_META = {
  bug:        { label: 'Bug',        icon: Bug,             cls: 'bg-red-50 text-red-700' },
  idea:       { label: 'Idea',       icon: Sparkles,        cls: 'bg-teal-50 text-teal-700' },
  compliment: { label: 'Compliment', icon: Heart,           cls: 'bg-pink-50 text-pink-700' },
  other:      { label: 'Other',      icon: MoreHorizontal,  cls: 'bg-stone-100 text-stone-700' },
};

const STATUS_TABS = [
  { id: '',            label: 'All' },
  { id: 'open',        label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'closed',      label: 'Resolved' },
];

function fmtDate(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'MMM d, h:mm a'); } catch { return iso; }
}

export default function AdminFeedbackPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [replyDraft, setReplyDraft] = useState({});  // {id: text}
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const url = statusFilter ? `${API_BASE}/admin/feedback?status=${statusFilter}` : `${API_BASE}/admin/feedback`;
      const { data } = await axios.get(url, { withCredentials: true });
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load feedback');
    } finally {
      setFetching(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (!user.is_owner) { navigate('/', { replace: true }); return; }
    load();
  }, [user, loading, navigate, load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await axios.patch(`${API_BASE}/admin/feedback/${id}`, { status }, { withCredentials: true });
      toast.success(`Marked ${status.replace('_', ' ')}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const sendReply = async (id) => {
    const text = (replyDraft[id] || '').trim();
    if (!text) {
      toast.error('Write a reply first');
      return;
    }
    setBusy(id);
    try {
      await axios.patch(
        `${API_BASE}/admin/feedback/${id}`,
        { admin_reply: text, status: 'closed' },
        { withCredentials: true },
      );
      toast.success('Reply sent + marked resolved');
      setReplyDraft((d) => ({ ...d, [id]: '' }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reply');
    } finally {
      setBusy(null);
    }
  };

  if (loading || (fetching && items.length === 0)) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="admin-feedback-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="text-3xl font-semibold text-stone-900">Customer feedback</h1>
          <div className="flex items-center gap-2">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id || 'all'}
                onClick={() => setStatusFilter(t.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  t.id === statusFilter
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300'
                }`}
                data-testid={`tab-${t.id || 'all'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <Card className="p-8 text-center text-stone-500 rounded-2xl">No feedback in this view.</Card>
        ) : (
          <div className="space-y-3">
            {items.map((f) => {
              const meta = CATEGORY_META[f.category] || CATEGORY_META.other;
              const Icon = meta.icon;
              return (
                <Card key={f.id} className="p-5 rounded-2xl border-stone-200 bg-white" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="secondary" className={`text-xs ${meta.cls}`}>
                        <Icon className="w-3 h-3 mr-1" /> {meta.label}
                      </Badge>
                      <span className="text-sm font-medium text-stone-900 truncate">
                        {f.user_name || f.user_email}
                      </span>
                      <span className="text-xs text-stone-500 truncate hidden sm:inline">
                        &lt;{f.user_email}&gt;
                      </span>
                    </div>
                    <span className="text-xs text-stone-500 shrink-0">{fmtDate(f.created_at)}</span>
                  </div>

                  <p className="text-sm text-stone-700 whitespace-pre-wrap mb-3">{f.message}</p>

                  {f.admin_reply && (
                    <div className="mb-3 p-3 rounded-xl bg-teal-50/60 border border-teal-100">
                      <div className="text-xs font-semibold text-teal-800 mb-1">Your reply{f.replied_at ? ` · ${fmtDate(f.replied_at)}` : ''}</div>
                      <p className="text-sm text-teal-900 whitespace-pre-wrap">{f.admin_reply}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="text-xs bg-stone-100 text-stone-700">
                      {f.status || 'open'}
                    </Badge>
                    {f.status !== 'in_progress' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === f.id}
                        onClick={() => setStatus(f.id, 'in_progress')}
                        className="rounded-full text-xs"
                        data-testid={`mark-progress-${f.id}`}
                      >
                        Mark in progress
                      </Button>
                    )}
                    {f.status !== 'closed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === f.id}
                        onClick={() => setStatus(f.id, 'closed')}
                        className="rounded-full text-xs"
                        data-testid={`mark-closed-${f.id}`}
                      >
                        Mark resolved
                      </Button>
                    )}
                    {f.status !== 'open' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === f.id}
                        onClick={() => setStatus(f.id, 'open')}
                        className="rounded-full text-xs text-stone-500"
                      >
                        Reopen
                      </Button>
                    )}
                  </div>

                  {!f.admin_reply && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        value={replyDraft[f.id] || ''}
                        onChange={(e) => setReplyDraft((d) => ({ ...d, [f.id]: e.target.value }))}
                        placeholder="Reply to customer (will be marked resolved on send)…"
                        className="min-h-[80px] rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500 text-sm"
                        data-testid={`reply-${f.id}`}
                      />
                      <Button
                        size="sm"
                        disabled={busy === f.id || !(replyDraft[f.id] || '').trim()}
                        onClick={() => sendReply(f.id)}
                        className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                        data-testid={`send-reply-${f.id}`}
                      >
                        {busy === f.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                        Send reply
                      </Button>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
